// FlaggedCalls.js
// Phase D of USER_FLAGGED_CALL_IMPROVEMENT — user-facing management view.
// Lists the current user's flagged_calls and supports immediate delete.
//
// Auth model: this component uses the Firestore client SDK directly.
// The flagged_calls security rules let users read/delete their own docs by
// uid match; writes are server-only via /flag-call. So a delete here is just
// a Firestore deleteDoc — no Cloud Function needed.

import React, { useEffect, useState } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  deleteDoc,
  doc as firestoreDoc,
} from 'firebase/firestore';
import { db } from '../../FirebaseConfig';
import {
  List,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';

const formatDate = (ts) => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const STATUS_LABEL = {
  pending: 'Pending review',
  reviewed: 'Reviewed',
  converted_to_regression: 'Converted to test',
  deleted_by_user: 'Deleted',
};

const FlaggedCalls = ({ currentUser }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmTarget, setConfirmTarget] = useState(null); // { id, contact_name }
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!currentUser) return;
    const q = query(
      collection(db, 'flagged_calls'),
      where('uid', '==', currentUser.uid),
      orderBy('flagged_at', 'desc')
    );
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const next = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setItems(next);
        setLoading(false);
      },
      (err) => {
        console.error('FlaggedCalls: snapshot error', err);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [currentUser]);

  const handleDelete = async () => {
    if (!confirmTarget) return;
    setDeleting(true);
    try {
      await deleteDoc(firestoreDoc(db, 'flagged_calls', confirmTarget.id));
      setToast('Flagged call deleted.');
      setConfirmTarget(null);
    } catch (err) {
      console.error('FlaggedCalls: delete error', err);
      setToast('Could not delete. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '20px', color: '#999' }}>Loading flagged calls...</div>;
  }

  return (
    <div>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Calls you've flagged to help improve Maia. Each is kept for up to 30 days,
        then auto-deletes. Deleting here removes it from the AI analysis queue
        immediately.
      </Typography>

      {items.length === 0 ? (
        <div style={{ padding: '20px', color: '#999', textAlign: 'center' }}>
          No flagged calls yet. When something goes wrong on a call, tap
          "Help improve Maia" in Call History to flag it.
        </div>
      ) : (
        <List>
          {items.map((item) => (
            <div
              key={item.id}
              style={{
                marginBottom: '8px',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                overflow: 'hidden',
              }}
            >
              <ListItem
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="delete flagged call"
                    onClick={() => setConfirmTarget({ id: item.id, contact_name: item.contact_name })}
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 'bold' }}>{item.contact_name || 'Unknown'}</span>
                      <span style={{ fontSize: '12px', color: '#999' }}>{formatDate(item.flagged_at)}</span>
                    </div>
                  }
                  secondary={
                    <div>
                      {item.user_reason ? (
                        <div style={{ fontSize: '13px', color: '#444', marginTop: '4px' }}>
                          "{item.user_reason}"
                        </div>
                      ) : (
                        <div style={{ fontSize: '13px', color: '#999', fontStyle: 'italic', marginTop: '4px' }}>
                          (no reason provided)
                        </div>
                      )}
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        Status: {STATUS_LABEL[item.review_status] || item.review_status || 'pending'}
                      </div>
                    </div>
                  }
                />
              </ListItem>
            </div>
          ))}
        </List>
      )}

      <Dialog open={Boolean(confirmTarget)} onClose={() => setConfirmTarget(null)}>
        <DialogTitle>Delete this flagged call?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deleting this removes the call from the AI analysis queue.
            Deletion is immediate — once removed, it cannot be recovered.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default FlaggedCalls;
